import React from 'react';

const AppScreenshots = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            See AkiMoon in Action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the intuitive interface designed specifically for women's health tracking and personalized wellness insights.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16">
          {/* First iPhone Mockup - Cycle Tracking Screen */}
          <div className="relative">
            <div className="w-72 h-[580px] bg-black rounded-[3.5rem] p-3 shadow-2xl relative">
              {/* iPhone Frame Details */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl z-10"></div>
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-16 h-2 bg-gray-800 rounded-full"></div>
              
              <div className="w-full h-full bg-white rounded-[3rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="h-12 bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-between px-8 text-white text-sm font-medium relative">
                  <span className="font-semibold">9:41</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-5 h-2 bg-white rounded-sm"></div>
                    <div className="w-8 h-2 bg-white rounded-sm"></div>
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                
                {/* App Content */}
                <div className="p-6 h-full bg-gradient-to-br from-purple-50 to-pink-50">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Cycle Tracking</h3>
                    <p className="text-sm text-gray-600">Day 14 of 28</p>
                  </div>
                  
                  {/* Cycle Circle */}
                  <div className="flex justify-center mb-8">
                    <div className="relative w-40 h-40">
                      <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 120 120">
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          stroke="#8b5cf6"
                          strokeWidth="8"
                          strokeDasharray="339.292"
                          strokeDashoffset="169.646"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-gray-900">50%</div>
                          <div className="text-sm text-gray-600">Complete</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Symptoms */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="text-sm font-medium text-gray-900">Energy Level</span>
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div key={star} className={`w-4 h-4 rounded-full ${star <= 3 ? 'bg-yellow-400' : 'bg-gray-200'}`}></div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="text-sm font-medium text-gray-900">Mood</span>
                      <span className="text-sm text-purple-600 font-medium">Balanced</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
              <div className="bg-white px-4 py-2 rounded-full shadow-lg">
                <p className="text-sm font-medium text-gray-900">Cycle Tracking</p>
              </div>
            </div>
          </div>

          {/* Second iPhone Mockup - AI Recommendations Screen */}
          <div className="relative">
            <div className="w-72 h-[580px] bg-black rounded-[3.5rem] p-3 shadow-2xl relative">
              {/* iPhone Frame Details */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl z-10"></div>
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-16 h-2 bg-gray-800 rounded-full"></div>
              
              <div className="w-full h-full bg-white rounded-[3rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="h-12 bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-between px-8 text-white text-sm font-medium relative">
                  <span className="font-semibold">9:41</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-5 h-2 bg-white rounded-sm"></div>
                    <div className="w-8 h-2 bg-white rounded-sm"></div>
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                
                {/* App Content */}
                <div className="p-6 h-full bg-gradient-to-br from-purple-50 to-pink-50">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">AI Insights</h3>
                    <p className="text-sm text-gray-600">Personalized for you</p>
                  </div>
                  
                  {/* AI Recommendations */}
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Nutrition</span>
                      </div>
                      <p className="text-sm text-gray-600">Increase iron-rich foods like spinach and lentils</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Fitness</span>
                      </div>
                      <p className="text-sm text-gray-600">Light yoga or walking recommended today</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Wellness</span>
                      </div>
                      <p className="text-sm text-gray-600">Practice mindfulness for 10 minutes</p>
                    </div>
                  </div>
                  
                  {/* Bottom Navigation */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white rounded-full p-3 shadow-lg">
                      <div className="flex justify-around">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                          </svg>
                        </div>
                        <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
              <div className="bg-white px-4 py-2 rounded-full shadow-lg">
                <p className="text-sm font-medium text-gray-900">AI Recommendations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            Join thousands of women who are taking control of their health journey
          </p>
          <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            Join the Waitlist
          </button>
        </div>
      </div>
    </section>
  );
};

export default AppScreenshots; 